import { useState, useEffect, useCallback } from 'react'
import { Wifi, WifiOff, Lock, RefreshCw, X, CheckCircle2, Eye, EyeOff } from 'lucide-react'

interface WifiNetwork {
  ssid: string
  bssid: string
  signal: number
  auth: string
}

interface WifiStatus {
  ssid: string | null
  state: string
  signal: number
}

interface Props {
  onClose: () => void
}

function SignalBars({ pct }: { pct: number }) {
  const strength = pct >= 75 ? 4 : pct >= 50 ? 3 : pct >= 25 ? 2 : 1
  return (
    <div className="flex items-end gap-0.5 h-4">
      {[1,2,3,4].map(b => (
        <div
          key={b}
          className={`w-1.5 rounded-sm transition-colors ${b <= strength ? 'bg-blue-400' : 'bg-zinc-600'}`}
          style={{ height: `${b * 25}%` }}
        />
      ))}
    </div>
  )
}

export default function WifiPanel({ onClose }: Props) {
  const api = (window as any).electronAPI
  const isInElectron = !!api?.pingServer || !!api?.isElectron?.()
  const hasWifiSupport = !!api?.wifiList  // only exists in Electron v1.3.3+

  const [networks, setNetworks] = useState<WifiNetwork[]>([])
  const [status, setStatus] = useState<WifiStatus | null>(null)
  const [scanning, setScanning] = useState(false)
  const [connecting, setConnecting] = useState<string | null>(null)
  const [selected, setSelected] = useState<WifiNetwork | null>(null)
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const scan = useCallback(async () => {
    if (!hasWifiSupport) return
    setScanning(true)
    setError(null)
    try {
      const [nets, stat] = await Promise.all([
        api.wifiList(),
        api.wifiStatus(),
      ])
      const sorted = [...nets].sort((a: WifiNetwork, b: WifiNetwork) => b.signal - a.signal)
      setNetworks(sorted)
      setStatus(stat)
    } catch {
      setError('Scan mislukt')
    } finally {
      setScanning(false)
    }
  }, [hasWifiSupport, api])

  useEffect(() => { scan() }, [scan])

  const handleConnect = async () => {
    if (!selected || !hasWifiSupport) return
    setConnecting(selected.ssid)
    setError(null)
    setSuccess(null)
    try {
      const result = await api.wifiConnect(selected.ssid, password)
      if (result.success) {
        setSuccess(`Verbonden met ${selected.ssid}`)
        setSelected(null)
        setPassword('')
        setTimeout(scan, 500)
      } else {
        setError(result.error || 'Verbinding mislukt')
      }
    } catch {
      setError('Verbinding mislukt')
    } finally {
      setConnecting(null)
    }
  }

  const isSecure = (auth: string) => !auth.toLowerCase().includes('open')
  const isConnected = (ssid: string) => status?.ssid === ssid

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700/60">
          <div className="flex items-center gap-2.5">
            <Wifi className="h-5 w-5 text-blue-400" />
            <div>
              <p className="text-sm font-bold text-white">WiFi Netwerken</p>
              {status?.ssid && (
                <p className="text-[11px] text-zinc-400">Verbonden: <span className="text-blue-300 font-medium">{status.ssid}</span></p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasWifiSupport && (
              <button
                onClick={scan}
                disabled={scanning}
                className="flex items-center gap-1.5 rounded-lg bg-zinc-800 px-2.5 py-1.5 text-[11px] font-semibold text-zinc-300 hover:bg-zinc-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${scanning ? 'animate-spin' : ''}`} />
                {scanning ? 'Scannen...' : 'Vernieuwen'}
              </button>
            )}
            <button onClick={onClose} className="rounded-lg bg-zinc-800 p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Not in Electron at all */}
        {!isInElectron && (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-zinc-500 text-center">
            <WifiOff className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm font-semibold text-zinc-300">WiFi beheer</p>
            <p className="text-xs mt-1">Alleen beschikbaar in de desktop-app.</p>
          </div>
        )}

        {/* In Electron but old version (no WiFi IPC) */}
        {isInElectron && !hasWifiSupport && (
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <div className="h-12 w-12 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-3">
              <Wifi className="h-6 w-6 text-amber-400" />
            </div>
            <p className="text-sm font-bold text-white">Update vereist</p>
            <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
              WiFi-beheer vereist <strong className="text-white">PoseBox Kiosk v1.3.3</strong> of hoger.<br />
              Herstart de app om de update te installeren.
            </p>
            <button
              onClick={onClose}
              className="mt-5 rounded-xl bg-zinc-800 hover:bg-zinc-700 px-5 py-2 text-sm font-semibold text-white transition-colors"
            >
              Sluiten
            </button>
          </div>
        )}

        {/* Full WiFi manager (v1.3.3+) */}
        {isInElectron && hasWifiSupport && !selected && (
          <div className="max-h-72 overflow-y-auto divide-y divide-zinc-700/40">
            {scanning && networks.length === 0 && (
              <div className="flex items-center justify-center py-10 text-zinc-500 gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm">Netwerken zoeken...</span>
              </div>
            )}
            {!scanning && networks.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-zinc-500">
                <WifiOff className="h-6 w-6 mb-1.5 opacity-40" />
                <p className="text-sm">Geen netwerken gevonden</p>
              </div>
            )}
            {networks.map((n) => (
              <button
                key={n.ssid}
                onClick={() => { setSelected(n); setPassword(''); setError(null) }}
                className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-zinc-800 ${isConnected(n.ssid) ? 'bg-blue-500/10' : ''}`}
              >
                <SignalBars pct={n.signal} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{n.ssid}</p>
                  <p className="text-[10px] text-zinc-500">{n.auth}</p>
                </div>
                {isSecure(n.auth) && <Lock className="h-3.5 w-3.5 text-zinc-500 shrink-0" />}
                {isConnected(n.ssid) && <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0" />}
              </button>
            ))}
          </div>
        )}

        {/* Connect form */}
        {isInElectron && hasWifiSupport && selected && (
          <div className="px-5 py-5 space-y-4">
            <div className="flex items-center gap-2">
              <button onClick={() => { setSelected(null); setError(null) }} className="text-zinc-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
              <p className="text-sm font-semibold text-white">Verbinden met <span className="text-blue-300">{selected.ssid}</span></p>
            </div>
            {isSecure(selected.auth) && (
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="WiFi wachtwoord"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleConnect()}
                  autoFocus
                  className="w-full rounded-xl bg-zinc-800 border border-zinc-700 px-4 py-3 pr-11 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            )}
            {error && <p className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
            {success && <p className="text-xs text-green-400 bg-green-500/10 rounded-lg px-3 py-2">{success}</p>}
            <button
              onClick={handleConnect}
              disabled={!!connecting || (isSecure(selected.auth) && !password)}
              className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:pointer-events-none px-4 py-3 text-sm font-bold text-white transition-colors flex items-center justify-center gap-2"
            >
              {connecting ? (
                <><RefreshCw className="h-4 w-4 animate-spin" /> Verbinden...</>
              ) : 'Verbinden'}
            </button>
          </div>
        )}

        {/* Status bar */}
        {isInElectron && hasWifiSupport && status && (
          <div className="px-5 py-2.5 border-t border-zinc-700/60 bg-zinc-800/40 flex items-center justify-between">
            <span className="text-[11px] text-zinc-500">Status: <span className="text-zinc-300">{status.state}</span></span>
            {status.signal > 0 && (
              <span className="text-[11px] text-zinc-500">Signaal: <span className="text-zinc-300">{status.signal}%</span></span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
